import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProviderService } from '../../../generated/graphql';
import ServiceCard from '../ServiceCard';

interface SortableServiceCardProps {
  service: ProviderService;
  providerPrimaryColor?: string | null;
  handleDeleteService?: (service: ProviderService) => void;
}

const SortableServiceCard: React.FC<SortableServiceCardProps> = ({
  service,
  providerPrimaryColor,
  handleDeleteService,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: service.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.6 : 1,
    scale: isDragging ? 0.95 : 1,
    zIndex: isDragging ? 50 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    boxShadow: isDragging
      ? '0 12px 32px rgba(0, 0, 0, 0.25)'
      : '0 2px 8px rgba(0, 0, 0, 0.1)',
  };

  // Don't allow dragging archived services
  // Note: archived services are filtered out in the query, but we check anyway
  const isArchived = false; // Archived services won't appear in sortable list

  const dragListeners = isArchived ? {} : listeners;
  const dragAttributes = isArchived ? {} : attributes;

  // Handle click navigation - only navigate if not dragging
  const handleClick = (e: React.MouseEvent) => {
    // If the drag operation is active, don't navigate
    if (isDragging) {
      return;
    }
    navigate(`/services/${service.id}`, {
      state: { from: location.pathname },
    });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleClick}
      className={isDragging ? 'z-50' : ''}
    >
      <div {...dragAttributes} {...dragListeners}>
        <ServiceCard
          service={service}
          providerPrimaryColor={providerPrimaryColor}
          handleDeleteService={handleDeleteService}
        />
      </div>
    </div>
  );
};

export default SortableServiceCard;
